import React from 'react'
import { useFormikContext } from 'formik'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { Checkbox, Dropdown, KnockFormDropdown, PhoneNumberInputFormik, TextField } from '@carsys/form'
import { Box, Divider, Skeleton, Stack, Typography } from '@mui/material'
import { CountriesDropdownFormik } from '@carsys/form/src/CountriesDropdownFormik'
import { IDistributorResponseData, IWholesalerField } from '@cbgms/api'
import { CmsSystem, TicketSystem } from '@cbgms/api/modules/enums'
import { EmptyUUID } from '@cbgms/base/utils/uuid'
import { COUNTRYOPTIONS } from 'core/constants/countries'
import { getDistributors } from 'app-support/licenses/state/actions'
import { FormValues } from './Form'
import { useGetWholesalerOrderSystem } from '../hooks/useGetWholesalerOrderSystem'
import AvailableForPortalToggle from './AvailableForPortalToggle'
import { CountryCode } from '@carsys/enums/country-code'
import MockResponseDataToggle from './MockResponseDataToggle'

const spacing = 1
const dividerStyle = { marginY: 2 }

const GeneralFields: React.FC = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { values: formValues, setFieldValue, resetForm, initialValues, setValues } = useFormikContext<FormValues>()

  const { data: orderSystemData, isLoading: orderSystemIsLoading } = useGetWholesalerOrderSystem(
    formValues.Country,
    formValues.OrderSystem,
    formValues.OrderSystemSettings
  )

  React.useEffect(() => {
    if (orderSystemData) {
      resetForm({ values: { ...initialValues, OrderSystemSettings: orderSystemData.OrderSystemSettings } })
      setValues({ ...formValues, OrderSystemSettings: orderSystemData.OrderSystemSettings })
    }
  }, [orderSystemData])

  const getDistributorOptions = React.useCallback(async params => {
    return dispatch(getDistributors(params))
  }, [])

  return (
    <Box>
      <Stack spacing={spacing}>
        <Typography variant='h4'>{t('wholesale.general_title')}</Typography>
        <Box display='flex' gap={4}>
          <Checkbox name='IsActive' label={t('wholesale.active')} />
          <AvailableForPortalToggle />
          <MockResponseDataToggle />
        </Box>
        <TextField required name='Name' label={t('wholesale.name')} />
        <CountriesDropdownFormik
          required
          countryOptions={COUNTRYOPTIONS}
          name='Country'
          label={t('wholesale.select_country')}
          onChange={() => {
            setFieldValue('OrderSystem', '')
          }}
        />
        {orderSystemIsLoading && <LoadingSkeleton />}
        {!orderSystemIsLoading && orderSystemData && (
          <>
            <Dropdown
              label={t('wholesale.select_ordersystem')}
              name='OrderSystem'
              required
              options={orderSystemData.WholesalerOrderSytemDropdownOptions}
            />
            <DynamicWholesalerFields
              orderSystemSettings={formValues.OrderSystemSettings}
              wholesalerField={orderSystemData.WholesalerOrderSystemFields}
            />
          </>
        )}
      </Stack>

      <Divider variant='middle' sx={dividerStyle} />
      <Stack spacing={spacing}>
        <Typography variant='h6'>{t('wholesale.general_contact_and_terms_title')}</Typography>
        <TextField
          label={t('wholesale.contact_support_email')}
          title={t('wholesale.contact_support_email_info_title')}
          name='SupportEmail'
        />
        <PhoneNumberInputFormik
          defaultCountry={formValues.Country || CountryCode.NL} // we default to our biggest userbase NL
          label={t('wholesale.contact_support_phonenumber')}
          title={t('wholesale.contact_support_phonenumber_info_title')}
          name='SupportPhoneNumber'
        />
        <TextField
          label={t('wholesale.terms_and_conditions_url')}
          title={t('wholesale.terms_and_conditions_url_info_title')}
          name='TermsAndConditionsURL'
        />
      </Stack>
      <Divider variant='middle' sx={dividerStyle} />
      <Stack spacing={spacing}>
        <Typography variant='h6'>{t('wholesale.general_ticket_system_title')}</Typography>
        <Dropdown
          label={t('wholesale.ticket_system')}
          name='TicketSystem'
          clearButton
          options={[
            { text: 'Zendesk', value: TicketSystem.Zendesk },
            { text: 'Email', value: TicketSystem.Email }
          ]}
        />
        {formValues.TicketSystem && <TextField label={t('wholesale.ticket_agent_email')} name='TicketAgentEmail' required />}

        {formValues.TicketSystem === TicketSystem.Zendesk && (
          <>
            <TextField name='TicketURL' required label={t('wholesale.ticket_url')} />
            <TextField name='TicketAPIKey' required label={t('wholesale.ticket_api_key')} />
            <TextField name='TicketBrandID' required label={t('wholesale.ticket_brand_id')} />
          </>
        )}
      </Stack>

      <Divider variant='middle' sx={dividerStyle} />
      <Stack spacing={spacing}>
        <Typography variant='h6'>{t('wholesale.general_cms_title')}</Typography>
        <Dropdown
          label={t('wholesale.cms_selection')}
          name='CMSSystem'
          clearButton
          options={[
            { text: 'Wordpress', value: CmsSystem.WordPress },
            { text: 'RAW', value: CmsSystem.Raw },
            { text: 'Drupal JSONAPI', value: CmsSystem.DrupalJSONAPI }
          ]}
        />
        {!!formValues.CMSSystem && <TextField label={t('wholesale.cms_url')} name='CMSURL' />}
        {!!formValues.CMSSystem && formValues.CMSSystem === CmsSystem.DrupalJSONAPI && (
          <TextField label={t('wholesale.cms_drupal_override')} name='CMSDrupalOverride' />
        )}
      </Stack>
      <Divider variant='middle' sx={dividerStyle} />
      <Stack spacing={spacing}>
        <Typography variant='h6'>{t('wholesale.general_HaynesPro_license_title')}</Typography>

        <KnockFormDropdown<IDistributorResponseData>
          label={t('wholesale.haynes_pro_distributor_uuid')}
          name='HaynesProDistributorUUID'
          text={row => row.Distributor.Name}
          value={row => row.Distributor.UUID}
          action={getDistributorOptions}
          emptyValue={EmptyUUID}
          fields={['Distributor.UUID', 'Distributor.Name']}
          clearButton
        />
      </Stack>
    </Box>
  )
}

const DynamicWholesalerFields: React.FC<{
  wholesalerField: { [k: string]: IWholesalerField } | undefined
  orderSystemSettings: { [k: string]: string }
}> = ({ wholesalerField, orderSystemSettings }) => {
  const { t } = useTranslation()

  if (!wholesalerField) return null

  return (
    <>
      {Object.keys(orderSystemSettings || {}).map(
        key =>
          Object.hasOwn(wholesalerField, key) && (
            <TextField
              key={key}
              name={`OrderSystemSettings.${wholesalerField[key].Name}`}
              required={wholesalerField[key].IsRequired}
              label={t(wholesalerField[key].DisplayName)}
              data-testid={`${wholesalerField[key].Name}-input`}
            />
          )
      )}
    </>
  )
}

const LoadingSkeleton = () => {
  const height = 30
  return (
    <>
      <Box p={2}>
        <Skeleton height={height} variant='rectangular' />
      </Box>
      <Box p={2}>
        <Skeleton height={height} variant='rectangular' />
      </Box>
      <Box p={2}>
        <Skeleton height={height} variant='rectangular' />
      </Box>
    </>
  )
}
export default GeneralFields
